import { useRouter } from "@core/Api/useRouter";
import { useArticlePropsStore } from "@core-ui/stores/ArticlePropsStore/ArticlePropsStore.provider";
import { useCallback, useEffect, useRef } from "react";

// The panel shows a deleted article from the old revision (`<catalog>:<scope>/…`); without the diff
// query its address leads nowhere.
const splitRevisionPath = (articlePath: string | undefined) => {
	const [catalogPart, ...rest] = articlePath?.split("/") ?? [];
	return catalogPart?.includes(":") ? rest.join("/") : null;
};

export const useLeaveDeletedArticle = (isOpen: boolean) => {
	const router = useRouter();
	const routerRef = useRef(router);
	routerRef.current = router;
	const articlePath = useArticlePropsStore((state) => state.data?.ref?.path);
	const revisionFile = splitRevisionPath(articlePath);
	const revisionFileRef = useRef(revisionFile);
	revisionFileRef.current = revisionFile;
	const returnPath = useRef<string>(null);
	const restored = useRef(false);

	// Keyed by the article, not the address: the address changes before the article props arrive.
	useEffect(() => {
		restored.current = false;
		if (isOpen && articlePath && !revisionFile) returnPath.current = routerRef.current.path;
	}, [isOpen, articlePath, revisionFile]);

	// Discard opens the restored article itself; leaving on close would race it.
	const noteDiscard = useCallback((paths: string[]) => {
		if (revisionFileRef.current && paths.includes(revisionFileRef.current)) restored.current = true;
	}, []);

	/** Returns `true` when it moved to the last article that still exists. */
	const leave = useCallback((keepDiff: boolean) => {
		if (!revisionFileRef.current || restored.current || !returnPath.current) return false;
		const query = keepDiff
			? { diff: "1", oldScope: "HEAD", scope: undefined }
			: { diff: undefined, oldScope: undefined, scope: undefined };
		void routerRef.current.pushPath(returnPath.current, query);
		return true;
	}, []);

	return { leave, noteDiscard };
};
